import React, { useState, useRef, useEffect } from "react";
import { Input, Flex, Divider, Button, Row, Col } from "antd";
const { TextArea } = Input;

interface Finding {
  lineNumber: number;
  organ: string;
  text: string;
  cumulativeEditTime: number | null; // Renamed from editTotalTime
}

interface Edit {
  editStartTime: Date;
  editEndTime: Date;
  editedBy: "user";
  editType: "insert" | "update" | "delete";
  editDetails: {
    organ: string;
    text: string;
  };
}

interface ReportRecord {
  examination: string;
  comparison: string;
  technique: string;
  findings: Finding[];
  impressions: string;
  historyOfEdits: Edit[];
}

const db = () => {
  // Define the report
  const reportRecord = {
    examination: "CT chest without contrast.",
    comparison: "None.",
    technique: "Axial images were obtained through the chest.",
    findings: [
      {
        lineNumber: 6,
        organ: "Liver",
        text: "[]",
        cumulativeEditTime: 0,
      },
      {
        lineNumber: 7,
        organ: "Gallbladder",
        text: "[Cholelithiasis, cholecystitis, polyps]",
        cumulativeEditTime: 0,
      },
      {
        lineNumber: 8,
        organ: "Pancreas",
        text: "[Pancreatitis, neoplasms, ductal abnormalities]",
        cumulativeEditTime: 0,
      },
      {
        lineNumber: 9,
        organ: "Spleen",
        text: "[Size, density, trauma effects, infarcts]",
        cumulativeEditTime: 0,
      },
      {
        lineNumber: 10,
        organ: "Kidneys",
        text: "[Size, shape, stones, masses, cysts]",
        cumulativeEditTime: 0,
      },
      {
        lineNumber: 11,
        organ: "Adrenal Glands",
        text: "[Lesions, enlargement]",
        cumulativeEditTime: 0,
      },
      {
        lineNumber: 12,
        organ: "Bowel",
        text: "[Obstruction, wall thickening, inflammation, ischemia]",
        cumulativeEditTime: 0,
      },
      {
        lineNumber: 13,
        organ: "Bladder",
        text: "[Wall abnormalities, masses]",
        cumulativeEditTime: 0,
      },
    ],
    impressions:
      "The CT chest without contrast demonstrates no evidence of acute pulmonary disease. There are findings suggestive of chronic lung changes and cardiomegaly, which may require further clinical correlation and follow-up. Incidental findings as noted above.",
    historyOfEdits: [],
  };

  // Return the updated report
  return reportRecord;
};

const RadiologyReport = () => {
  const [reportRecord, setReportRecord] = useState<ReportRecord | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [findings, setFindings] = useState<ReportRecord["findings"]>([]);
  const [impressions, setImpressions] = useState<string>("");
  const containerFindingRef = useRef<HTMLDivElement>(null);
  const containerImpressionsRef = useRef<HTMLDivElement>(null);
  // State to capture report start time, inference time, and report end time
  const [reportStartTime, setReportStartTime] = useState<Date | null>(null);
  const [inferenceTime, setInferenceTime] = useState<Date | null>(null);
  const [reportEndTime, setReportEndTime] = useState<Date | null>(null);
  // State to capture the edit times
  const [editStartTime, setEditStartTime] = useState<Date | null>(null);

  // function to handle edit start
  const handleEditStartTime = (index: number, organ: string) => {
    setEditStartTime(new Date());
  };

  useEffect(() => {
    // Capture the report start time as soon as the component mounts
    const startTime = new Date();
    setReportStartTime(startTime);

    // Simulate fetching data from the database
    const simulateDbFetch = (): Promise<ReportRecord> => {
      return new Promise((resolve) => {
        setTimeout(() => {
          // Simulate a delay in fetching data from the database
          const fetchedReportRecord: ReportRecord = db(); // Assume db() fetches your data
          resolve(fetchedReportRecord);
        }, 1); // 1 micro second delay
      });
    };

    simulateDbFetch().then((fetchedReportRecord) => {
      // Once data is fetched, capture the inference end time
      const inferenceEndTime = new Date();
      setInferenceTime(inferenceEndTime);

      // Update the state with the fetched report
      setReportRecord(fetchedReportRecord);
      setFindings(fetchedReportRecord.findings); // Now set findings
      setImpressions(fetchedReportRecord.impressions); // Now set impressions

      // Update loading state
      setIsLoading(false);
    });
  }, []);

  const handleDoubleClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    e.stopPropagation(); // Stop the event from propagating further
    // Since e.currentTarget refers to the element to which the event handler is attached,
    // and considering the handler is attached to a textarea, it's safe to assert this way.
    const textArea = e.currentTarget;
    textArea.focus(); // Ensure the element is focused
    textArea.select(); // Then select the text
  };

  // // Function to handle finding change
  // const handleFindingChange = (index: any, newText: any) => {
  //   const updatedFindings = [...findings];
  //   updatedFindings[index].text = newText;
  //   setFindings(updatedFindings);
  // };

  const handleFindingChange = (index: number, newText: string) => {
    setReportRecord((prevReport) => {
      if (!prevReport) return null; // Guard against null previous state

      const updatedFindings = [...prevReport.findings];
      updatedFindings[index] = {
        ...updatedFindings[index],
        text: newText,
      };

      // Create the edit object here based on your logic
      const newEdit: Edit = {
        editStartTime: editStartTime ? editStartTime : new Date(),
        editEndTime: new Date(), // Example; replace with actual logic
        editedBy: "user",
        editType: "update", // Example; adjust based on your logic
        editDetails: {
          organ: updatedFindings[index].organ,
          text: newText,
        },
      };

      setFindings(updatedFindings); // Update the local state

      return {
        ...prevReport,
        findings: updatedFindings,
        historyOfEdits: [...prevReport.historyOfEdits, newEdit],
      };
    });
  };

  // Function to handle impressions change
  const handleImpressionsChange = (newText: any) => {
    setImpressions(newText);
  };

  // Function to handle save button click
  const handleSaveReport = () => {
    setReportEndTime(new Date()); // Capture the report end time when the user clicks save
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh" }}>
        Artificial Intelligence model inference currently underway...
      </div>
    );
  }

  return (
    <Row gutter={16}>
      <Col span={16}>
        <Flex vertical gap="middle">
          <h5>RADIOLOGY REPORT</h5>
          {/* EXAMINATIONS */}
          <div>
            <strong>EXAMINATION:</strong> {reportRecord?.examination}
          </div>

          {/* COMPARISON */}
          <div>
            <strong>COMPARISON:</strong> {reportRecord?.comparison}
          </div>

          {/* TECHNIQUE */}
          <div>
            <strong>TECHNIQUE:</strong> {reportRecord?.technique}
          </div>

          {/* FINDINGS */}
          <div>
            <strong>FINDINGS:</strong>
          </div>
          <Flex vertical gap="middle">
            {findings.map((line, index) => (
              <Flex key={index}>
                <div style={{ minWidth: "200px" }}>{`${line.organ}: `}</div>
                <div ref={containerFindingRef} style={{ width: "100%" }}>
                  <TextArea
                    value={line.text}
                    onChange={(e) => handleFindingChange(index, e.target.value)}
                    onDoubleClick={handleDoubleClick}
                    onFocus={() => handleEditStartTime(index, line.organ)}
                    autoSize={{ minRows: 1, maxRows: 5 }}
                    style={{ border: "1px solid #f0f0f0" }}
                  />
                </div>
              </Flex>
            ))}
          </Flex>

          {/* IMPRESSION */}
          <Flex align="center">
            <div style={{ minWidth: "200px" }}>
              <strong>IMRESSIONS:</strong>
            </div>
            <div ref={containerImpressionsRef} style={{ width: "100%" }}>
              <TextArea
                value={impressions}
                onChange={(e) => handleImpressionsChange(e.target.value)}
                onDoubleClick={handleDoubleClick}
                autoSize={{ minRows: 1, maxRows: 5 }}
                style={{ border: "1px solid #f0f0f0" }}
              />
            </div>
          </Flex>

          <div>
            {/* Save Button */}
            <Button onClick={handleSaveReport}>Save</Button>
          </div>
        </Flex>
      </Col>
      <Col span={8}>
        <div style={{padding: '8px', border: '1px solid #f0f0f0'}}>
          {/* TIME MEASUREMENTS */}
          <Flex vertical gap={"2px"}>
            <h5 style={{textAlign: 'center'}}>TIME MEASUREMENTS</h5>
            <div>
              <strong>Report Start Time:</strong>{" "}
              {reportStartTime?.toLocaleString()}
            </div>
            {/* <div>
              <strong>AI Model Inference End Time:</strong>{" "}
              {inferenceTime?.toLocaleString()}
            </div> */}
            <div>
              <strong>Report End Time:</strong>{" "}
              {reportEndTime
                ? reportEndTime.toLocaleString()
                : "Reporting in progress..."}
            </div>
          
            <h5 style={{textAlign: 'center'}}>EDIT HISTORY</h5>
            <div>
              {reportRecord && reportRecord.historyOfEdits.length > 0 ? (
                <ul>
                  {reportRecord.historyOfEdits.map((edit, index) => (
                    <li key={index}>
                      <div>
                        <strong>Edited By:</strong> {edit.editedBy}
                      </div>
                      <div>
                        <strong>Edit Type:</strong> {edit.editType}
                      </div>
                      <div>
                        <strong>Organ:</strong> {edit.editDetails.organ}
                      </div>
                      <div>
                        <strong>Text:</strong> {edit.editDetails.text}
                      </div>
                      <div>
                        <strong>Edit Start Time:</strong>{" "}
                        {edit.editStartTime.toLocaleString()}
                      </div>
                      <div>
                        <strong>Edit End Time:</strong>{" "}
                        {edit.editEndTime.toLocaleString()}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No edits have been made yet.</p>
              )}
            </div>
          </Flex>
        </div>
      </Col>
    </Row>
  );
};

export default RadiologyReport;
